import React, { useCallback } from "react";
import PropTypes from "prop-types";
import Analytics from "lib/Analytics";
import { FormControlLabel, Radio, Typography, withStyles } from "@theconversation/ui";
import FormFieldset from "components/FormFieldset";
import i18n from "lib/i18n";

const styles = {
  methodFieldset: {
    marginBottom: -10,
  },
  paymentProvider: {
    overflow: "visible",
    margin: "0 auto 20px",
  },
};

const PaymentProviderChooser = ({
  paymentProvider,
  active,
  classes,
  creditCardAvailable,
  directDebitAvailable,
  payPalAvailable,
  onChange,
}) => {
  const numProviders = [
    creditCardAvailable,
    directDebitAvailable,
    payPalAvailable,
  ].filter(p => p).length;

  if (numProviders < 2) {
    return null;
  }

  const ccLabel = `${i18n.t("donation.label.credit_card")} (${i18n.t("donation.hint.accepted_credit_cards")})`;
  const handleChange = useCallback(
    (e) => {
      Analytics.trackFormEvent("Donation Form", "Completed", "Payment Method", e.target.value);
      onChange({ paymentProvider: e.target.value });
    },
    [Analytics, onChange]
  );

  const creditCard = creditCardAvailable ? (
    <fieldset className={classes.methodFieldset}>
      <FormControlLabel
        control={
          <Radio
            aria-label={i18n.t("donation.label.credit_card")}
            checked={paymentProvider === "credit_card"}
            color="primary"
            disabled={!active}
            name="payment_method"
            onChange={handleChange}
            value="credit_card"
          />
        }
        label={ccLabel}
        labelPlacement="end"
        value="credit_card"
      />
    </fieldset>
  ) : null;

  const directDebit = directDebitAvailable ? (
    <fieldset className={classes.methodFieldset}>
      <FormControlLabel
        control={
          <Radio
            aria-label={i18n.t("donation.label.direct_debit")}
            checked={paymentProvider === "direct_debit"}
            color="primary"
            disabled={!active}
            name="payment_method"
            onChange={handleChange}
            value="direct_debit"
          />
        }
        label={i18n.t("donation.label.direct_debit")}
        labelPlacement="end"
        value="direct_debit"
      />
    </fieldset>
  ) : null;

  const paypal = payPalAvailable ? (
    <fieldset>
      <FormControlLabel
        control={
          <Radio
            aria-label="Paypal"
            checked={paymentProvider === "paypal"}
            color="primary"
            disabled={!active}
            name="payment_method"
            onChange={handleChange}
            value="paypal"
          />
        }
        label="PayPal"
        labelPlacement="end"
        value="paypal"
      />
    </fieldset>
  ) : null;

  return (
    <div className={classes.paymentProvider} id="payment_method">
      <FormFieldset>
        <Typography variant="h6">{i18n.t("donation.heading.payment_method")}</Typography>
      </FormFieldset>

      {creditCard}
      {directDebit}
      {paypal}
    </div>
  );
};

export default React.memo(
  withStyles(styles)(PaymentProviderChooser)
);

PaymentProviderChooser.defaultProps = {
  active: true,
  onChange: () => {},
  creditCardAvailable: false,
  directDebitAvailable: false,
  payPalAvailable: false,
  paymentProvider: "credit_card",
};

PaymentProviderChooser.propTypes = {
  active: PropTypes.bool,
  classes: PropTypes.shape({
    methodFieldset: PropTypes.string,
    paymentProvider: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func,
  creditCardAvailable: PropTypes.bool,
  directDebitAvailable: PropTypes.bool,
  payPalAvailable: PropTypes.bool,
  paymentProvider: PropTypes.string,
};
